import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { env } from "@/app/config/env";

/**
 * TRANSLATIONS FIND THEMSELVES.
 *
 * Every feature keeps its own strings next to its code (`features/<domain>/locales/<lang>/
 * <namespace>.json`) and app-wide strings live in `app/locales`. This glob collects them at
 * build time, so adding a feature never means editing a central registry — the step people
 * forget, and whose symptom is a screen of raw keys in production.
 */
const modules = import.meta.glob<Record<string, unknown>>(
  ["../locales/*/*.json", "../../features/**/locales/*/*.json", "../../shared/**/locales/*/*.json"],
  { eager: true, import: "default" },
);

const resources: Record<string, Record<string, unknown>> = {};

for (const [path, contents] of Object.entries(modules)) {
  const match = path.match(/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, language, namespace] = match;
  resources[language] ??= {};
  resources[language][namespace] = contents;
}

/**
 * CALLED EXPLICITLY FROM main.tsx, never triggered by importing this module.
 *
 * A module that configures itself on import has to be imported for its side effect, and a
 * side-effect import exports nothing, binds nothing, and looks removable to any reader or
 * automated cleanup. When it is removed the app does not fail — it renders raw translation
 * keys. `no-side-effect-import` (eslint-rules/) makes that shape unavailable.
 */
export function initI18n() {
  if (i18next.isInitialized) return i18next;

  void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: resources as never,
    fallbackLng: env.defaultLocale,
    supportedLngs: env.locales,
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      // The phone's language first; a stored choice wins over it. No querystring or
      // subdomain detection — neither exists inside an app.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "app.language",
      caches: ["localStorage"],
    },
  });

  return i18next;
}

export { i18next };
